const { getPayloadCode, getPayloadMessage } = require('./response');
const { retrySeconds } = require('../utils/emailCooldown');

const details = {
  'This wallet already exists.': 'walletExists',
  'This wallet already exists. Contact support to restore it.': 'walletExists',
  'Wallet is disabled by operations; contact support.': 'withdrawSourceDisabled',
  'An invitation code is required.': 'invitationRequired',
  'Your bKash PIN is locked. Stop retrying and contact bKash support to unlock it before binding again.': 'walletPinLocked',
  'bKash has temporarily locked this device after multiple incorrect PIN attempts. Stop retrying and wait 24 hours from your last login attempt before binding again.': 'walletDeviceLocked',
  'Too many incorrect reset codes. Please try again in 10 minutes.': 'resetCodeLimit',
  'Enter a valid email address.': 'emailFormat',
  'Password must be at most 72 bytes.': 'passwordTooLong',
  'This referral code is invalid or inactive.': 'referralInvalid',
  'Referrals are currently unavailable.': 'referralUnavailable',
  'Password must be at least 8 characters and include both letters and numbers.': 'passwordFormat',
  'Paste the complete original SMS, including the verification text at the end. Do not enter only the code.': 'completeSms',
  'Wallet verification failed. Check your PIN and use the complete latest SMS. Cancel and bind again if it still fails.': 'walletVerification',
  'Wallet verification failed. Check your PIN and use the complete latest SMS.': 'walletVerification',
  'Failed to send the wallet SMS. Please wait and try binding again.': 'walletSmsFailed',
  'Failed to send the wallet SMS. Please wait and try again later.': 'walletSmsFailed',
  'Your wallet binding session has expired. Cancel and bind the wallet again.': 'bindingExpired',
  'This code has already been submitted. Cancel and bind again to request a new SMS.': 'codeSubmitted',
  'This code has already been submitted. Please request another SMS.': 'codeSubmitted',
  'This wallet is already bound.': 'walletBound',
  'This wallet is already in use by the platform.': 'walletInUse',
  'You have reached the wallet binding limit.': 'walletLimit',
  'Too many requests. Please try again later.': 'tooManyRequests',
  'Too many failed sign-in attempts. Please try again in 15 minutes.': 'loginLimit',
  "You have reached today's code limit. Please try again tomorrow.": 'emailCodeLimit',
  'Failed to send the verification code. Please try again later.': 'emailCodeFailed',
  'Your account is frozen. Please contact support.': 'accountFrozen',
  'Complete wallet verification before checking the balance.': 'verifyFirst',
  'Unable to check the wallet balance. Please try again later.': 'balanceFailed',
  'Fresh wallet balance is unavailable. Please retry.': 'balanceFailed',
  'Wallet changed during balance preflight. Please retry.': 'walletChanged',
  'Wallet session changed during balance preflight. Please retry.': 'walletChanged',
  'This wallet is disabled in the system. Contact support or choose another wallet.': 'withdrawSourceDisabled',
  'The wallet was updated. Refresh and try again.': 'walletChanged',
  'This wallet cannot verify an SMS in its current state. Refresh and try again.': 'walletChanged',
  'This wallet binding can no longer be cancelled. Refresh and try again.': 'walletChanged',
  'This wallet cannot go offline in its current state.': 'walletChanged',
  'Wallet binding is currently unavailable. Please try again later.': 'bindingUnavailable',
  'This Nagad wallet requires USSD verification. Please use another wallet.': 'ussdRequired',
  'Wallet number and PIN are required.': 'walletRequired',
  'This wallet type is not supported.': 'walletUnsupported',
  'PIN is required.': 'pinRequired',
  'Withdrawal amount must be greater than 0.': 'withdrawAmount',
  'Minimum withdrawal request is 100 BDT plus the withdrawal fee.': 'withdrawMinimum',
  'Target wallet number format is invalid.': 'targetWallet',
  'Target wallet type must match the source wallet type.': 'targetWalletType',
  'You cannot withdraw to the same wallet.': 'sameWallet',
  'This wallet is not activated yet.': 'verifyFirst',
  'This wallet status cannot recharge.': 'walletChanged',
  'Recharge amount must be greater than 0.': 'rechargeAmount',
  'Recharge amount supports up to two decimal places.': 'rechargeAmount',
  'Transaction ID is required.': 'rechargeTransaction',
  'Transaction ID is too long.': 'rechargeTransaction',
  'This transaction ID was already submitted with a different amount.': 'rechargeDuplicate',
  'Withdrawable balance is not enough.': '1008',
  'Email or password is incorrect.': '1001',
  'This email is already registered.': '1003',
  'The verification code is incorrect or expired.': '2001'
};

const knownCodes = new Set([999, 1001, 1002, 1003, 1004, 1006, 1008, 1009, 1016, 1018, 2001, 401, 1288, 1289]);

const cancellationDetails = {
  'Wallet session could not be verified. Please contact support before restoring it.': 'cancelOfflineSession',
  'Wallet state changed. Please refresh and try again.': 'walletChanged',
  'Wallet balance does not match the records. Please contact support before restoring it.': 'cancelOfflineBalance',
  'Wallet does not exist.': 'cancelOfflineMissing',
  'Your account is frozen or unavailable.': 'cancelOfflineAccount',
  'Only a pending offline request can be cancelled.': 'cancelOfflinePending',
  'The offline cancellation window has ended.': 'cancelOfflineExpired',
  'This offline request cannot be restored automatically. Please contact support.': 'cancelOfflineSnapshot',
  'Settlement, withdrawal or risk checks prevent restoring this wallet.': 'cancelOfflineRestricted',
  'The wallet is unavailable or has another restriction. Please contact support.': 'cancelOfflineUnavailable'
};

function getErrorKey(payload, route = '') {
  const message = getPayloadMessage(payload);
  if (['/crowd/wallet/offline', '/crowd/wallet/resume'].includes(route.split('?')[0])) {
    const code = Number(getPayloadCode(payload));
    if ([401, 1288, 1289].includes(code)) return `errors.${code}`;
    return 'errors.businessStateFailed';
  }
  if (['/crowd/wallet/pauseReceive', '/crowd/wallet/resumeReceive'].includes(route.split('?')[0])) {
    const code = Number(getPayloadCode(payload));
    if ([401, 1288, 1289].includes(code)) return `errors.${code}`;
    return 'errors.receiveChangeFailed';
  }
  if (route.split('?')[0] === '/crowd/wallet/cancelOffline') {
    const code = Number(getPayloadCode(payload));
    if ([401, 1288, 1289].includes(code)) return `errors.${code}`;
    return Object.prototype.hasOwnProperty.call(cancellationDetails, message)
      ? `errors.${cancellationDetails[message]}` : 'errors.cancelOfflineFailed';
  }
  if (message === 'Too many requests. Please try again later.' && payload.data && payload.data.limitType === 'EMAIL_COOLDOWN' && retrySeconds(payload) !== null)
    return 'errors.emailCodeCooldown';
  if (Object.prototype.hasOwnProperty.call(details, message)) {
    return `errors.${details[message]}`;
  }
  // Older servers can still return provider diagnostics; never display those verbatim.
  if (typeof message === 'string' && (message.includes('11_0036_139') ||
      (message.includes('temporarily locked due to multiple incorrect PIN attempts') && message.includes('24 hours')))) {
    return 'errors.walletDeviceLocked';
  }
  if (typeof message === 'string' && message.includes('PIN_LOCKED')) {
    return 'errors.walletPinLocked';
  }
  if (typeof message === 'string' && /otp\/verify|reg\/login/.test(message)) {
    return 'errors.walletVerification';
  }
  if (typeof message === 'string' && /otp\/send/.test(message)) {
    return 'errors.walletSmsFailed';
  }
  const code = Number(getPayloadCode(payload));
  return knownCodes.has(code) ? `errors.${code}` : 'errors.requestFailed';
}

function getErrorParams(payload) { return { seconds: retrySeconds(payload) }; }
module.exports = { getErrorKey, getErrorParams, details };
